import { TemplateVariableDefinitionDto } from '@/features/dashboard/messageTemplates/types/TemplateVariableDefinitionDto.ts';

/**
 * Matches a variable that is *output* by the template — `{{ key }}` — mirroring
 * the backend's `Template.collect(TagType.VAR)`. The negative lookahead skips
 * everything that is not a plain output tag:
 *   `{{#if x}}` / `{{/if}}` (block guards), `{{! ... }}` (comments),
 *   `{{> partial}}`, `{{{raw}}}`, `{{& raw}}`, `{{= ... }}` and the
 *   `{{image 'id'}}` helper (space + quote fail the `[\w.]+` body).
 * The full dotted path is captured (`summary.url`, never just `url`), and the
 * pattern scans the raw wire string so it also matches inside attributes
 * (`href="{{ summary.url }}"`) and inside `{{#if x}}{{ x }}{{/if}}`.
 */
const OUTPUT_VARIABLE_PATTERN = /\{\{\s*(?![#/!>{&=])([\w.]+)\s*}}/g;

/**
 * Returns the set of variable keys the given template body *outputs*.
 * Semantics match the backend `collect(TagType.VAR)`.
 */
export const collectOutputVariables = (body: string): Set<string> => {
  const out = new Set<string>();
  for (const match of body.matchAll(OUTPUT_VARIABLE_PATTERN)) {
    out.add(match[1]);
  }
  return out;
};

/**
 * Keys the backend marks as required for the current channel/type.
 */
export const getRequiredVariableKeys = (
  variables?: TemplateVariableDefinitionDto[],
): string[] => variables?.filter((v) => v.required).map((v) => v.key) ?? [];

/**
 * Required keys that the body does not output — the ones the backend would
 * reject with `TEMPLATE_MISSING_REQUIRED_VARIABLE`.
 */
export const getMissingRequiredKeys = (
  body: string,
  requiredKeys: string[],
): string[] => {
  const used = collectOutputVariables(body);
  return requiredKeys.filter((key) => !used.has(key));
};
